import type {
  SlrGoodStandingReason,
  SlrGoodStandingState,
  SlrLoanStandingAction,
  SlrLoanStandingAuditState,
  SlrLoanStandingPresentation,
  SlrLoanStandingStatus,
} from 'tio-employee/types/slr-loan-standing-verification';

const ACTION_TO_CTA_KEY: Partial<Record<SlrLoanStandingAction, string>> = {
  upload_document: 'slr.loan_standing.actions.upload_document',
  upload_newer_document: 'slr.loan_standing.actions.upload_newer_document',
  confirm_name_match: 'slr.loan_standing.actions.confirm_name_match',
  contact_support: 'slr.loan_standing.actions.contact_support',
};

const STATUS_TO_TITLE_KEY: Record<SlrLoanStandingStatus, string> = {
  unknown: 'slr.loan_standing.title.pending_upload',
  pending_upload: 'slr.loan_standing.title.pending_upload',
  processing: 'slr.loan_standing.title.processing',
  eligible: 'slr.loan_standing.title.eligible',
  ineligible: 'slr.loan_standing.title.ineligible',
  try_again: 'slr.loan_standing.title.try_again',
  cannot_determine: 'slr.loan_standing.title.cannot_determine',
};

const STATUS_TO_DESCRIPTION_KEY: Record<SlrLoanStandingStatus, string> = {
  unknown: 'slr.loan_standing.description.pending_upload',
  pending_upload: 'slr.loan_standing.description.pending_upload',
  processing: 'slr.loan_standing.description.processing',
  eligible: 'slr.loan_standing.description.eligible',
  ineligible: 'slr.loan_standing.description.ineligible',
  try_again: 'slr.loan_standing.description.try_again',
  cannot_determine: 'slr.loan_standing.description.cannot_determine',
};

// tio-api `good_standing_reason` values → existing description translation keys.
// Copy is reused as-is (no YAML changes); `doc_too_old` intentionally covers
// both the stale-document and outside-audit-window reasons. `none` and
// `manual_override` produce no alert, so they fall through to the status-based
// description. Source of truth: tio-api EmployeePlanAccount GOOD_STANDING_REASON_*.
const REASON_TO_DESCRIPTION_KEY: Record<string, string> = {
  missing_document: 'slr.loan_standing.description.pending_upload',
  stale_document: 'slr.loan_standing.description.doc_too_old',
  outside_audit_window: 'slr.loan_standing.description.doc_too_old',
  missing_required_observations: 'slr.loan_standing.description.insufficient_observations',
  name_mismatch: 'slr.loan_standing.description.name_mismatch',
  status_not_in_good_standing: 'slr.loan_standing.description.not_in_good_standing',
  delinquent_or_collections: 'slr.loan_standing.description.not_in_good_standing',
  defaulted: 'slr.loan_standing.description.not_in_good_standing',
  parent_plus_only: 'slr.loan_standing.description.all_parent_plus',
  undeterminable: 'slr.loan_standing.description.cannot_determine',

  // Document-processing/extraction errors. These are NOT `good_standing_reason`
  // values — they would originate from the ObservableDocument pipeline, whose
  // surface to the UI is unconfirmed (see tio-front-end#8714 / decision D4).
  // Retained pending that confirmation rather than deleted.
  nslds_required_for_federal_non_repayment_status:
    'slr.loan_standing.description.nslds_required_for_federal_non_repayment_status',
  statement_required_for_repayment_status:
    'slr.loan_standing.description.statement_required_for_repayment_status',
  unsupported_document_for_status: 'slr.loan_standing.description.unsupported_document_for_status',
  unsupported_format: 'slr.loan_standing.description.unsupported_format',
  extraction_failed: 'slr.loan_standing.description.extraction_failed',
};

const INTENT_BY_STATUS: Record<SlrLoanStandingStatus, SlrLoanStandingPresentation['intent']> = {
  eligible: 'success',
  processing: 'info',
  unknown: 'warning',
  pending_upload: 'warning',
  ineligible: 'warning',
  try_again: 'warning',
  cannot_determine: 'warning',
};

/**
 * Maps tio-api's raw `good_standing` state (+ `good_standing_reason`) to the
 * frontend presentation status. tio-api owns 4 states; the UI distinguishes a
 * few more (pending-upload vs. try-again vs. cannot-determine). `processing` is
 * a client-only transient status and is never derived here.
 *
 * Source of truth: tio-api EmployeePlanAccount GOOD_STANDING_STATE_* /
 * GOOD_STANDING_REASON_*.
 */
export function deriveSlrLoanStandingStatus(
  goodStanding: SlrGoodStandingState,
  reason?: SlrGoodStandingReason | null
): SlrLoanStandingStatus {
  switch (goodStanding) {
    case 'eligible':
      return 'eligible';
    case 'unknown':
      return 'pending_upload';
    case 'undeterminable':
      return 'cannot_determine';
    case 'ineligible':
      if (reason === 'missing_document') return 'pending_upload';
      if (reason === 'stale_document' || reason === 'outside_audit_window') return 'try_again';
      // Recoverable second-document asks (doc pipeline): the reported status
      // needs an NSLDS file / current statement — the user can act, so this is
      // try_again (upload) rather than hard ineligibility (contact support).
      if (
        reason === 'nslds_required_for_federal_non_repayment_status' ||
        reason === 'statement_required_for_repayment_status'
      ) {
        return 'try_again';
      }
      return 'ineligible';
  }
}

/**
 * Derives the primary CTA action from the presentation status: not-yet-uploaded
 * → upload, recoverable (stale / outside-window / cannot-determine) → re-upload,
 * hard ineligibility → contact support. The per-reason policy can be refined
 * once the upload/observation contract lands (D4); this is a sensible default.
 */
export function deriveSlrLoanStandingAction(
  goodStanding: SlrGoodStandingState,
  reason?: SlrGoodStandingReason | null
): SlrLoanStandingAction {
  switch (deriveSlrLoanStandingStatus(goodStanding, reason)) {
    case 'pending_upload':
      return 'upload_document';
    case 'try_again':
    case 'cannot_determine':
      return 'upload_newer_document';
    case 'ineligible':
      return 'contact_support';
    case 'eligible':
    case 'processing':
    case 'unknown':
      return 'none';
  }
}

export function presentationForSlrLoanStanding(
  state: SlrLoanStandingAuditState
): SlrLoanStandingPresentation {
  const primaryReason = state.primaryReason ?? state.reasons?.[0] ?? state.failures?.[0]?.code;

  return {
    titleKey: STATUS_TO_TITLE_KEY[state.status],
    descriptionKey:
      (primaryReason && REASON_TO_DESCRIPTION_KEY[primaryReason]) ||
      STATUS_TO_DESCRIPTION_KEY[state.status],
    ctaKey: ACTION_TO_CTA_KEY[state.action],
    intent: INTENT_BY_STATUS[state.status],
    requiresAttention:
      state.action !== 'none' && state.status !== 'eligible' && state.status !== 'processing',
  };
}

export function shouldShowSlrLoanStandingAlert(state?: SlrLoanStandingAuditState | null) {
  if (!state?.featureEnabled) return false;
  const presentation = presentationForSlrLoanStanding(state);
  return presentation.requiresAttention || state.status === 'processing';
}

// tio-payment validation-error keys the good-standing verdict OWNS outright:
// whenever a verification verdict exists for the receiving account, the
// loan-status / borrower-name story belongs to the verification card (which
// offers the actionable path — upload / confirm / contact support), and the
// parallel payment-pipeline card is suppressed.
// Convergence rationale + full overlap map: tio-front-end#9811.
export const VERDICT_OWNED_VALIDATION_KEYS = ['INVALID_LOAN_STATUS', 'NAME_MISMATCH'];

// Suppressed only when the verdict's reason is parent_plus_only — the payment
// key also covers loan-type rules the verification domain does not own.
export const PARENT_PLUS_VALIDATION_KEYS = ['INVALID_LOAN_TYPE', 'AGGREGATION_INVALID_LOAN_TYPE'];

const ALL_OWNED_KEYS = new Set([...VERDICT_OWNED_VALIDATION_KEYS, ...PARENT_PLUS_VALIDATION_KEYS]);

// Action-item ids as minted by reimagined-data's assembly (`validation-${key}`).
const VERDICT_OWNED_VALIDATION_IDS = new Set(
  VERDICT_OWNED_VALIDATION_KEYS.map((key) => `validation-${key}`)
);
const PARENT_PLUS_VALIDATION_IDS = new Set(
  PARENT_PLUS_VALIDATION_KEYS.map((key) => `validation-${key}`)
);

/**
 * Order raw payment validation errors so verdict-owned keys come LAST before
 * the assembler's one-card-per-category dedup. Without this, a verdict-owned
 * key (e.g. INVALID_LOAN_STATUS) can claim its category's only slot, the
 * payment-only key sharing that category (e.g. EXCEEDED_LIMIT) is discarded
 * at assembly, and the convergence filter then suppresses the sole surviving
 * card — orphaning an unrelated failure. Category copy is identical per
 * issue type, so this reordering is invisible when no verdict exists.
 */
export function deprioritizeVerdictOwnedValidationErrors<T extends { key?: string }>(
  errors: T[]
): T[] {
  const owned = (error: T) => ALL_OWNED_KEYS.has(error.key ?? '');
  return [...errors.filter((error) => !owned(error)), ...errors.filter(owned)];
}

/**
 * Drop payment-pipeline action items that duplicate the loan-standing
 * verdict's domain (#9811). No ACTIVE verdict (feature disabled, outage, or
 * no receiving account) → items pass through untouched, so the payment
 * pipeline remains the fallback messenger.
 */
export function suppressVerdictOwnedActionItems<T extends { id: string }>(
  items: T[],
  state?: SlrLoanStandingAuditState | null
): T[] {
  if (!state?.featureEnabled) return items;
  return items.filter((item) => {
    if (VERDICT_OWNED_VALIDATION_IDS.has(item.id)) return false;
    if (state.primaryReason === 'parent_plus_only' && PARENT_PLUS_VALIDATION_IDS.has(item.id)) {
      return false;
    }
    return true;
  });
}
